// These values match keys which come from BE so don't change them
import { FinancialsColumnDefinition, FinancialsRowDataType } from '../../components/financials-table/types'

export enum EarningValueProperty {
  EpsActual = 'epsActual',
  EpsAnalysts = 'epsAnalysts',
  EpsEstimate = 'epsEstimate',
  EpsReportedActual = 'epsReportedActual',
  EpsReportedAnalysts = 'epsReportedAnalysts',
  EpsReportedEstimate = 'epsReportedEstimate',
  SalesActual = 'salesActual',
  SalesAnalysts = 'salesAnalysts',
  SalesEstimate = 'salesEstimate',
  Surprise = 'surprise',
}

export type Earning = {
  earningsDate: string | null
  epsActual: number | null
  epsEstimate: number | null
  fiscalEndDate: string
  fiscalPeriod: string
  ticker: string
} & Record<EarningValueProperty, number | null>

export interface EarningsDataType extends Omit<FinancialsRowDataType, 'id' | 'values'> {
  id: EarningValueProperty
  values: Array<{ year: string; value: number | null; isEstimate: boolean }>
  isEstimate?: boolean
}

export enum EarningsEstimateType {
  eps = 'E',
  epsGaap = 'R',
  sales = 'S',
}

export enum EarningsDataEnum {
  analysts = 'analysts',
  mainChange = 'mainChange',
  mainChangePct = 'mainChangePct',
  extraChangeYoYonQoQ = 'extraChangeYoYonQoQ',
  extraChangePctYoYonQoQ = 'extraChangePctYoYonQoQ',
}

export enum EarningsTimeframeEnum {
  annual = 'annual',
  quarterly = 'quarterly',
}

type EarningRowDataType = Omit<FinancialsRowDataType, 'values'> & {
  values: Array<{ year: string; value: number | null; percentageValue: number | null; isEstimate?: boolean }>
}

export type EarningsColumnsType = {
  columns: FinancialsColumnDefinition<EarningRowDataType>[]
  subColumns: Record<EarningsDataEnum, FinancialsColumnDefinition[]>
}

export type EarningPriceReaction = {
  date: string
  prevPrice: number
  price: number
  priceDiff: number
  spyPriceDiff: number
}

export type EarningPriceReactions = {
  [K: string]: EarningPriceReaction
}

export type EarningReportReaction = {
  ticker: string
  reportDate: string
  fiscalPeriod: string
  fiscalEndDate: string | null
  chartStartDate: string
  chartEndDate: string
  rsi: number | null
  reactions: EarningPriceReactions
}

export enum PriceReactionWhen {
  MINUS_DAYS_3 = 'minus_3_days',
  MINUS_DAYS_2 = 'minus_2_days',
  MINUS_DAYS_1 = 'minus_1_day',
  OPEN = 'open',
  HIGH = 'high',
  LOW = 'low',
  CLOSE = 'close',
  PLUS_DAYS_1 = 'plus_1_day',
  PLUS_DAYS_2 = 'plus_2_days',
  PLUS_DAYS_3 = 'plus_3_days',
  PLUS_WEEK_1 = 'plus_1_week',
  MINUS_WEEK_1 = 'minus_1_week',
}

export interface EarningsCommonData {
  earningsDate: string | null
  earningsAnnualData: Earning[]
  earningsData: Earning[]
  priceReactionData: EarningReportReaction[]
}

export type EarningsDataSettings = {
  [EarningsDataEnum.mainChange]: boolean
  [EarningsDataEnum.mainChangePct]: boolean
  [EarningsDataEnum.extraChangeYoYonQoQ]: boolean
  [EarningsDataEnum.extraChangePctYoYonQoQ]: boolean
}

export type EarningsSettings = EarningsDataSettings & {
  estimateType: EarningsEstimateType
  fiscalPeriod: string
  timeframe: EarningsTimeframeEnum
}
